import React from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";

function fmtDateTime(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function fmtDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getTrainerName(row) {
  return (
    `${row?.trainer?.person?.first_name || ""} ${row?.trainer?.person?.last_name || ""}`.trim() ||
    "-"
  );
}

function getLeadStageName(row) {
  return row?.leadStage?.lead_stage_name || row?.lead_stage?.lead_stage_name || "-";
}

export default function LeadActivitySnapshot() {
  const navigate = useNavigate();
  const location = useLocation();
  const { leadId } = useParams();
  const { userData } = useAuthStore();
  const organizationId = userData?.organization?.organization_id;

  const [loading, setLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [leadProfile, setLeadProfile] = React.useState(null);
  const [leadName, setLeadName] = React.useState("");

  const leadIds = React.useMemo(() => {
    const list = location?.state?.leadIds;
    if (!Array.isArray(list)) return [];
    return list
      .map((id) => String(id).trim())
      .filter((id) => id && id !== "-");
  }, [location?.state?.leadIds]);

  const currentLeadIndex = React.useMemo(
    () => leadIds.findIndex((id) => id === String(leadId)),
    [leadIds, leadId]
  );
  const hasPrevLead = currentLeadIndex > 0;
  const hasNextLead =
    currentLeadIndex !== -1 && currentLeadIndex < leadIds.length - 1;

  const navigateToLeadAt = React.useCallback(
    (index) => {
      if (index < 0 || index >= leadIds.length) return;
      const targetLeadId = leadIds[index];
      navigate(`/lead-activities/snapshot/${encodeURIComponent(targetLeadId)}`, {
        state: { leadIds },
      });
    },
    [leadIds, navigate]
  );

  React.useEffect(() => {
    if (!organizationId || !leadId) return;
    let isActive = true;

    const load = async () => {
      setLoading(true);
      setErrorText("");
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${MAIN_URL}/api/ems/leads/${encodeURIComponent(String(leadId))}/activities`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            params: {
              organization_id: organizationId,
            },
          }
        );

        // Preferred backend shape:
        // { success, message, data: { lead: {...}, activities: [...] } }
        const payload = res?.data?.data;
        const leadObj =
          payload?.lead ??
          (Array.isArray(payload) ? payload?.[0]?.lead : null) ??
          null;
        const activities = Array.isArray(payload?.activities)
          ? payload.activities
          : Array.isArray(payload)
            ? payload
            : Array.isArray(res?.data)
              ? res.data
              : [];
        if (!isActive) return;
        setRows(activities);
        const leadFromActivities = activities?.[0]?.lead || null;
        if (leadObj || leadFromActivities) {
          const resolvedLead = leadObj || leadFromActivities;
          setLeadProfile(resolvedLead);
          setLeadName(resolvedLead?.person_full_name || "");
        } else {
          // No activities: still fetch lead profile so snapshot shows lead data.
          try {
            const leadRes = await axios.get(
              `${MAIN_URL}/api/ems/leads/${encodeURIComponent(String(leadId))}`,
              {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                params: { organization_id: organizationId },
              }
            );
            const leadObj =
              leadRes?.data?.data?.lead ||
              leadRes?.data?.data ||
              leadRes?.data?.lead ||
              null;
            if (leadObj) {
              setLeadProfile(leadObj);
              setLeadName(leadObj?.person_full_name || "");
            } else {
              setLeadProfile(null);
              setLeadName("");
            }
          } catch {
            setLeadProfile(null);
            setLeadName("");
          }
        }
      } catch (err) {
        if (!isActive) return;
        setRows([]);
        setLeadProfile(null);
        setErrorText(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch lead snapshot."
        );
      } finally {
        if (!isActive) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [organizationId, leadId]);

  const latestActivity = React.useMemo(() => {
    if (!rows.length) return null;
    const sorted = [...rows].sort(
      (a, b) =>
        new Date(b?.activity_datetime || 0).getTime() -
        new Date(a?.activity_datetime || 0).getTime()
    );
    return sorted[0] || null;
  }, [rows]);

  const sortedActivities = React.useMemo(() => {
    if (!rows.length) return [];
    return [...rows].sort(
      (a, b) =>
        new Date(b?.activity_datetime || 0).getTime() -
        new Date(a?.activity_datetime || 0).getTime()
    );
  }, [rows]);

  const leadInfo = leadProfile || latestActivity?.lead || {};
  const city = leadInfo?.city?.city_name || leadInfo?.city_name || "-";
  const state = leadInfo?.state?.state_name || leadInfo?.state_name || "-";
  const country = leadInfo?.country?.country_name || leadInfo?.country_name || "-";
  const program =
    leadInfo?.interested_program?.program_name ||
    leadInfo?.training_program?.training_program_name ||
    latestActivity?.interested_program_name ||
    "-";
  const programCategory =
    leadInfo?.training_program_category?.training_program_category_name ||
    leadInfo?.training_program?.training_program_category?.training_program_category_name ||
    "-";
  const currentStage = getLeadStageName(latestActivity) || leadInfo?.lead_stage?.lead_stage_name || "-";

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f6f8", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate("/leads")}>
          Back
        </Button>
        <IconButton
          size="small"
          onClick={() => navigateToLeadAt(currentLeadIndex - 1)}
          disabled={!hasPrevLead}
          aria-label="Previous lead"
          sx={{ fontWeight: 900, fontSize: 22 }}
        >
          {"<"}
        </IconButton>
        <IconButton
          size="small"
          onClick={() => navigateToLeadAt(currentLeadIndex + 1)}
          disabled={!hasNextLead}
          aria-label="Next lead"
          sx={{ fontWeight: 900, fontSize: 22 }}
        >
          {">"}
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Lead Activity Snapshot
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lead ID: {leadId}
        {leadName ? ` | Lead Name: ${leadName}` : ""}
      </Typography>

      {errorText ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText}
        </Typography>
      ) : null}

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 3,
                    mb: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Lead Person Name
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {leadName || "-"}
                    </Typography>
                    <Typography color="text.secondary">{city}</Typography>
                    <Typography color="text.secondary">
                      {country !== "-" ? country : state}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Interested Program
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {programCategory}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {program}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Lead Come on:</strong> {fmtDate(leadInfo?.lead_datetime || latestActivity?.activity_datetime)}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Stage:</strong> {currentStage}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Activities
                </Typography>
                {sortedActivities.length ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {sortedActivities.map((r, index) => (
                      <Box
                        key={r.organization_ems_lead_activity_id || index}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                          Activity {index + 1}
                        </Typography>

                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Activity Date</TableCell>
                                <TableCell>Activity Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Activity By</TableCell>
                                <TableCell>Activity Stage</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>{fmtDateTime(r.activity_datetime)}</TableCell>
                                <TableCell>{r.activity_type || "-"}</TableCell>
                                <TableCell>{r.call_status || "-"}</TableCell>
                                <TableCell>{getTrainerName(r)}</TableCell>
                                <TableCell>{getLeadStageName(r)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>
                          Activity Summary
                        </Typography>
                        <Typography color="text.secondary">
                          {r?.activity_summary || "No activity summary available."}
                        </Typography>

                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>
                          Remarks
                        </Typography>
                        <Typography color="text.secondary">
                          {r?.remarks || "No remarks available."}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No lead activities found for this lead.
                  </Typography>
                )}
              </CardContent>
            </Card>

          </Box>
        )}
      </Paper>
    </Box>
  );
}
